import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Camera, Image, LogIn, LogOut, Languages, History, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFreeTrial } from '@/hooks/useFreeTrial';
import { useEvaluation } from '@/contexts/EvaluationContext';
import { toast } from 'sonner';
import { usePremiumCredits } from '@/hooks/usePremiumCredits';
import { PremiumCreditsDisplay } from '@/components/PremiumCreditsDisplay';
import { HowItWorks } from '@/components/HowItWorks';
import { TrendsPreview } from '@/components/TrendsPreview';
import { FAQ } from '@/components/FAQ';
import { SocialProof } from '@/components/SocialProof';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import logo from '@/assets/logo.png';

const Index = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { hasUsedFreeTrial, canUseFreeTrial } = useFreeTrial();
  const { startFreeTrial } = useEvaluation();
  const { hasPremium, creditsRemaining, loading: creditsLoading } = usePremiumCredits();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const handleOpenCamera = async () => {
    if (user) {
      navigate('/camera');
    } else {
      const canUse = await canUseFreeTrial();
      if (!canUse) {
        toast.error('Você já usou seu teste grátis! Faça login para continuar.');
        navigate('/auth');
      } else {
        toast.success('🎄 Você tem 1 avaliação grátis!');
        startFreeTrial();
        navigate('/camera', { state: { freeTrialMode: true } });
      }
    }
  };

  const handleSelectFromGallery = async () => {
    if (user) {
      // User logged in - proceed directly
      openGalleryAndNavigate();
    } else {
      const canUse = await canUseFreeTrial();
      if (!canUse) {
        toast.error('Você já usou seu teste grátis! Faça login para continuar.');
        navigate('/auth');
      } else {
        toast.success('🎄 Você tem 1 avaliação grátis!');
        startFreeTrial();
        openGalleryAndNavigate(true);
      }
    }
  };

  const openGalleryAndNavigate = (freeTrialMode = false) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          navigate('/result', { 
            state: { 
              image: event.target?.result,
              freeTrialMode 
            } 
          });
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-snow via-background to-pine-dark/5 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 text-6xl animate-pulse opacity-30">🎄</div>
      <div className="absolute top-32 right-16 text-4xl animate-pulse opacity-20" style={{ animationDelay: '1s' }}>⭐</div>
      <div className="absolute bottom-32 left-20 text-5xl animate-pulse opacity-25" style={{ animationDelay: '0.5s' }}>🎁</div>
      <div className="absolute bottom-20 right-24 text-3xl animate-pulse opacity-30" style={{ animationDelay: '1.5s' }}>❄️</div>

      {/* Language selector */}
      <div className="absolute top-4 right-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-foreground" aria-label="Select language">
              <Languages className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => changeLanguage('en')}>
                🇬🇧 English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage('es')}>
                🇪🇸 Español
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage('pt')}>
                🇧🇷 Português
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage('it')}>
                🇮🇹 Italiano
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage('de')}>
                🇩🇪 Deutsch
              </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Login/Profile button */}
      <div className="absolute top-4 left-4 flex flex-col gap-2">
        <div className="flex gap-2">
          {user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/history')}
                className="text-muted-foreground"
              >
                <History className="h-4 w-4 mr-2" />
                {t('landing.history', 'History')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={signOut}
                className="text-muted-foreground"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('landing.logout', 'Logout')}
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/auth')}
              className="text-muted-foreground"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {t('landing.login')}
            </Button>
          )}
        </div>
        {user && (
          <PremiumCreditsDisplay 
            hasPremium={hasPremium}
            creditsRemaining={creditsRemaining}
            loading={creditsLoading}
          />
        )}
      </div>

      <div className="max-w-md w-full space-y-8 animate-fade-in">
        <div className="text-center space-y-4">
          <img 
            src={logo} 
            alt="TreeRated10 / Árvore Nota 10" 
            className="w-40 h-40 mx-auto mb-4 animate-scale-in object-contain"
          />
          <h1 className="text-4xl md:text-5xl font-bold text-pine-dark leading-tight">
            {t('landing.title')}
          </h1>
          <p className="text-lg text-muted-foreground">
            {t('landing.subtitle')}
          </p>
        </div>

        <div className="space-y-4 pt-8">
          <Button
            onClick={handleOpenCamera}
            size="lg"
            className="w-full h-16 text-lg font-semibold bg-gradient-to-r from-primary to-pine-light hover:opacity-90 text-primary-foreground shadow-pine transition-all hover:scale-105"
          >
            <Camera className="mr-2 h-6 w-6" />
            {t('landing.cameraButton')}
            {!user && !hasUsedFreeTrial() && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-gold text-pine rounded-full font-semibold">
                GRÁTIS
              </span>
            )}
          </Button>

          <Button
            onClick={handleSelectFromGallery}
            variant="outline"
            size="lg"
            className="w-full h-14 text-base font-medium border-2 border-primary/20 hover:border-primary/40 hover:bg-primary/5"
          >
            <Image className="mr-2 h-5 w-5" />
            {t('landing.galleryButton')}
            {!user && !hasUsedFreeTrial() && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-gold text-pine rounded-full font-semibold">
                GRÁTIS
              </span>
            )}
          </Button>

          <Button
            onClick={() => navigate('/quiz')}
            variant="outline"
            size="lg"
            className="w-full h-14 text-base font-medium border-2 border-primary/30 hover:border-primary/50 hover:bg-primary/10 bg-gradient-to-r from-primary/5 to-primary/10"
          >
            <Sparkles className="mr-2 h-5 w-5 text-primary" />
            {t('landing.quizButton')}
            <span className="ml-2 px-2 py-0.5 text-xs bg-primary text-primary-foreground rounded-full font-semibold">
              2025
            </span>
          </Button>
        </div>

        <div className="text-center pt-4">
          <p className="text-sm text-muted-foreground">
            {window.location.hostname.includes('arvorenota10') ? 'arvorenota10.com.br' : 'treerated10.com'}
          </p>
        </div>
      </div>

      {/* SEO Content Sections */}
      <div className="w-full space-y-12 mt-16">
        <HowItWorks />
        <TrendsPreview />
        <SocialProof />
        <FAQ />
      </div>
    </div>
  );
};

export default Index;
